import type {
  RecurringBackfillPreview,
  RecurringDonationSeries,
  RecurringEndCondition,
  RecurringStatus,
} from '@/types/recurringDonation'
import { getUkTaxYearPeriodKeyForDate } from '@/helpers/givingRecord'
import {
  RECURRING_BACKFILL_CONFIRM_THRESHOLD,
  RECURRING_BACKFILL_HARD_LIMIT,
} from '@/helpers/recurring/limits'
import {
  enumerateOccurrenceDates,
  nextOccurrenceAfter,
  occurrenceAtIndex,
} from '@/helpers/recurring/recurrence'
import { ukDateToDonationIso } from '@/helpers/recurring/dates'

export type RecurringExistingOccurrence = {
  scheduledOccurrenceDate: string
}

export type RecurringOccurrenceToCreate = {
  scheduledOccurrenceDate: string
  charity: string
  amountPence: number
  giftAidDeclared: boolean
  notes?: string
}

export type RecurringGenerationResult = {
  toCreate: RecurringOccurrenceToCreate[]
  nextDonationDate: string | null
  status: RecurringStatus
  completedAt?: string
}

function isPastEndDate(
  occurrenceDate: string,
  endCondition: RecurringEndCondition
): boolean {
  return (
    endCondition.type === 'on_date' && occurrenceDate > endCondition.endDate
  )
}

function hasReachedOccurrenceCap(
  processedCount: number,
  endCondition: RecurringEndCondition
): boolean {
  return (
    endCondition.type === 'after_occurrences' &&
    processedCount >= endCondition.occurrenceCount
  )
}

/**
 * Slots the cursor has already passed in the current segment. Matches the
 * API's consumption rule: a slot consumes once whether it was generated or
 * skipped, deleting its donation changes nothing, and a restart or schedule
 * change moves the anchor, so the count starts again.
 */
function consumedSlotCount(
  series: RecurringDonationSeries,
  cursor: string
): number {
  if (series.endCondition.type !== 'after_occurrences') return 0
  let count = 0
  while (
    count < series.endCondition.occurrenceCount &&
    occurrenceAtIndex(series.scheduleAnchorDate, series.frequency, count) <
      cursor
  ) {
    count += 1
  }
  return count
}

/** Mock donation id for one scheduled slot; unique per (series, date). */
export function recurringDonationId(
  seriesId: string,
  occurrenceDate: string
): string {
  return `manual-recurring-${seriesId}-${occurrenceDate}`
}

/**
 * Create due donor-added rows for an active series. Idempotent: existing
 * donations and skip/delete exceptions are never recreated.
 */
export function generateDueOccurrences(
  series: RecurringDonationSeries,
  existing: RecurringExistingOccurrence[],
  todayIso: string
): RecurringGenerationResult {
  if (series.status !== 'active' || !series.nextDonationDate) {
    return {
      toCreate: [],
      nextDonationDate: series.nextDonationDate,
      status: series.status,
    }
  }

  const existingDates = new Set(
    existing.map((row) => row.scheduledOccurrenceDate)
  )
  const exceptionDates = new Set(
    series.exceptions.map((row) => row.occurrenceDate)
  )
  const processedCount = consumedSlotCount(series, series.nextDonationDate)

  const toCreate: RecurringOccurrenceToCreate[] = []
  let cursor: string | null = series.nextDonationDate
  let status: RecurringStatus = series.status
  let processed = processedCount
  let completedAt: string | undefined

  const markCompleted = () => {
    status = 'completed'
    cursor = null
    completedAt = ukDateToDonationIso(todayIso)
  }

  // Walk from the stored next date. Dates already handled (donation or
  // exception) advance the cursor without creating a duplicate.
  while (cursor && cursor <= todayIso) {
    if (isPastEndDate(cursor, series.endCondition)) {
      markCompleted()
      break
    }
    if (hasReachedOccurrenceCap(processed, series.endCondition)) {
      markCompleted()
      break
    }

    const alreadyCreated = existingDates.has(cursor)
    const isException = exceptionDates.has(cursor)

    if (!alreadyCreated && !isException) {
      toCreate.push({
        scheduledOccurrenceDate: cursor,
        charity: series.charity,
        amountPence: series.amountPence,
        giftAidDeclared: series.giftAidDeclared,
        notes: series.notes,
      })
    }
    processed += 1

    const following = nextOccurrenceAfter(
      series.scheduleAnchorDate,
      series.frequency,
      cursor
    )

    if (
      !following ||
      isPastEndDate(following, series.endCondition) ||
      hasReachedOccurrenceCap(processed, series.endCondition)
    ) {
      markCompleted()
      break
    }

    cursor = following
  }

  return {
    toCreate,
    nextDonationDate: cursor,
    status,
    completedAt,
  }
}

function dueDatesForNewSeries(input: {
  firstDonationDate: string
  frequency: RecurringDonationSeries['frequency']
  endCondition: RecurringEndCondition
  todayIso: string
  hardLimit: number
}): string[] {
  const raw = enumerateOccurrenceDates({
    anchorIso: input.firstDonationDate,
    frequency: input.frequency,
    fromIso: input.firstDonationDate,
    throughIso: input.todayIso,
    hardLimit: input.hardLimit,
  })

  const capped =
    input.endCondition.type === 'after_occurrences'
      ? raw.slice(0, input.endCondition.occurrenceCount)
      : raw

  if (input.endCondition.type === 'on_date') {
    const endDate = input.endCondition.endDate
    return capped.filter((date) => date <= endDate)
  }
  return capped
}

/**
 * Count historical occurrences that would be created immediately on save.
 * Does not silently truncate: exceedsLimit means the API must reject.
 */
export function previewRecurringBackfill(input: {
  firstDonationDate: string
  frequency: RecurringDonationSeries['frequency']
  endCondition: RecurringEndCondition
  todayIso: string
}): RecurringBackfillPreview {
  const dates = dueDatesForNewSeries({
    ...input,
    hardLimit: RECURRING_BACKFILL_HARD_LIMIT,
  })
  const dueCount = dates.length
  const taxYears = [
    ...new Set(dates.map((date) => getUkTaxYearPeriodKeyForDate(date))),
  ].sort()

  return {
    dueCount,
    taxYears,
    fromDate: dates[0] ?? null,
    toDate: dates[dates.length - 1] ?? null,
    requiresConfirmation: dueCount > RECURRING_BACKFILL_CONFIRM_THRESHOLD,
    exceedsLimit: dueCount > RECURRING_BACKFILL_HARD_LIMIT,
    limit: RECURRING_BACKFILL_HARD_LIMIT,
    confirmThreshold: RECURRING_BACKFILL_CONFIRM_THRESHOLD,
  }
}

export function skipNextOccurrence(
  series: RecurringDonationSeries,
  todayIso: string
): { nextDonationDate: string | null; status: RecurringStatus } | null {
  if (series.status !== 'active' || !series.nextDonationDate) return null
  if (series.nextDonationDate < todayIso) return null

  const following = nextOccurrenceAfter(
    series.scheduleAnchorDate,
    series.frequency,
    series.nextDonationDate
  )

  if (!following || isPastEndDate(following, series.endCondition)) {
    return { nextDonationDate: null, status: 'completed' }
  }

  const processed = consumedSlotCount(series, series.nextDonationDate) + 1
  if (hasReachedOccurrenceCap(processed, series.endCondition)) {
    return { nextDonationDate: null, status: 'completed' }
  }

  return { nextDonationDate: following, status: 'active' }
}
